// Synthetic market data engine for forex, commodities, indices
// Now anchored to REAL prices via /api/market/realtime-quotes (open.er-api.com
// for forex + Finnhub ETF proxies for metals/energy/indices). Local ticks add
// smooth micro-movement between polls. Falls back to pure stochastic if the
// realtime endpoint is unreachable.

import axios from 'axios';

const REAL_POLL_MS = 3000; // refresh real anchor price every 3s (metals/forex/indices)
// Binance WS symbol used as a sub-second live anchor for XAU/USD. PAXG is a
// 1:1 gold-backed token so its spot ≈ OANDA:XAUUSD, matching the chart tick-
// by-tick instead of lagging 10-20s behind gold-api.com's REST feed.
const BINANCE_WS_ANCHORS = { XAUUSD: 'paxgusdt' };

const ASSETS = {
  // Forex majors
  'EURUSD':  { name: 'EUR/USD',    base: 1.1572,  vol: 0.00025, prec: 5, pip: 0.0001, class: 'forex' },
  'GBPUSD':  { name: 'GBP/USD',    base: 1.3541,  vol: 0.00030, prec: 5, pip: 0.0001, class: 'forex' },
  'USDJPY':  { name: 'USD/JPY',    base: 159.20,  vol: 0.030,   prec: 3, pip: 0.01,   class: 'forex' },
  'AUDUSD':  { name: 'AUD/USD',    base: 0.7084,  vol: 0.00028, prec: 5, pip: 0.0001, class: 'forex' },
  'USDCAD':  { name: 'USD/CAD',    base: 1.3873,  vol: 0.00025, prec: 5, pip: 0.0001, class: 'forex' },
  'USDCHF':  { name: 'USD/CHF',    base: 0.8127,  vol: 0.00025, prec: 5, pip: 0.0001, class: 'forex' },
  'NZDUSD':  { name: 'NZD/USD',    base: 0.5891,  vol: 0.00030, prec: 5, pip: 0.0001, class: 'forex' },
  'EURJPY':  { name: 'EUR/JPY',    base: 184.23,  vol: 0.035,   prec: 3, pip: 0.01,   class: 'forex' },
  'GBPJPY':  { name: 'GBP/JPY',    base: 215.58,  vol: 0.045,   prec: 3, pip: 0.01,   class: 'forex' },
  // Commodities
  'XAUUSD':  { name: 'XAU/USD',    base: 4375.00, vol: 1.80,    prec: 2, pip: 0.01,   class: 'metals' },
  'XAGUSD':  { name: 'XAG/USD',    base: 64.50,   vol: 0.060,   prec: 3, pip: 0.001,  class: 'metals' },
  'WTI':     { name: 'US OIL',     base: 64.00,   vol: 0.070,   prec: 2, pip: 0.01,   class: 'energy' },
  'BRENT':   { name: 'BRENT OIL',  base: 68.00,   vol: 0.070,   prec: 2, pip: 0.01,   class: 'energy' },
  'NATGAS':  { name: 'NAT GAS',    base: 4.300,   vol: 0.010,   prec: 3, pip: 0.001,  class: 'energy' },
  'COPPER':  { name: 'COPPER',     base: 4.900,   vol: 0.012,   prec: 3, pip: 0.001,  class: 'metals' },
  // Indices
  'US30':    { name: 'US30 DOW',   base: 39218,   vol: 8.5,     prec: 1, pip: 0.1,    class: 'index' },
  'SPX500':  { name: 'S&P 500',    base: 5205,    vol: 1.4,     prec: 2, pip: 0.01,   class: 'index' },
  'NAS100':  { name: 'NAS 100',    base: 18654,   vol: 6.8,     prec: 1, pip: 0.1,    class: 'index' },
  'UK100':   { name: 'UK 100',     base: 8125,    vol: 2.0,     prec: 1, pip: 0.1,    class: 'index' },
  'DE40':    { name: 'GERMANY 40', base: 18025,   vol: 5.5,     prec: 1, pip: 0.1,    class: 'index' },
  'JP225':   { name: 'NIKKEI',     base: 38450,   vol: 7.5,     prec: 1, pip: 0.1,    class: 'index' }
};

const state = {}; // symbol -> { price, dayOpen, dayHigh, dayLow, candles: { interval -> [] } }
const listeners = {}; // symbol -> { ticker: [], kline: {}, depth: [], trades: [] }
let heartbeatStarted = false;

// Deterministic pseudo-random based on seed
function seedRand(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

// Gaussian sample via Box-Muller
function gauss(rand) {
  const u = Math.max(rand(), 1e-9);
  const v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function intervalSeconds(iv) {
  const map = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400, '1d': 86400 };
  return map[iv] || 60;
}

// Generate historical candles ending at "now"
function generateHistory(symbol, interval, limit = 300) {
  const asset = ASSETS[symbol];
  if (!asset) return [];
  const ivSec = intervalSeconds(interval);
  const now = Math.floor(Date.now() / 1000);
  const startTime = Math.floor(now / ivSec) * ivSec - (limit - 1) * ivSec;
  const rand = seedRand(hashCode(symbol) + Math.floor(now / 86400)); // seed by day so consistent within day

  let price = asset.base * (1 + gauss(rand) * asset.vol * 20 / asset.base); // start with some drift
  const candles = [];
  // Volatility scale per bar (higher for longer TFs)
  const barVolMult = Math.sqrt(ivSec / 60);
  const barVol = asset.vol * barVolMult;

  for (let i = 0; i < limit; i++) {
    const time = startTime + i * ivSec;
    const drift = gauss(rand) * barVol * 0.3;
    const open = price;
    // Intra-bar movement: multiple ticks with gentle mean-reversion so the
    // 300-bar series stays in a realistic band instead of wandering away.
    let hi = open, lo = open, cl = open;
    for (let t = 0; t < 20; t++) {
      cl += gauss(rand) * barVol / Math.sqrt(20);
      cl += (asset.base - cl) * 0.015; // pull toward the anchor base each sub-tick
      if (cl > hi) hi = cl;
      if (cl < lo) lo = cl;
    }
    cl += drift;
    if (cl > hi) hi = cl;
    if (cl < lo) lo = cl;
    price = cl;
    // Volume: correlated with range
    const vol = Math.abs(hi - lo) / barVol * (1000 + rand() * 5000);
    candles.push({ time, open, high: hi, low: lo, close: cl, volume: vol });
  }
  return candles;
}

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function ensureState(symbol) {
  if (state[symbol]) return state[symbol];
  const asset = ASSETS[symbol];
  const history = {};
  for (const iv of ['1m', '5m', '15m', '1h', '4h', '1d']) {
    history[iv] = generateHistory(symbol, iv, 300);
  }
  const last = history['1m'][history['1m'].length - 1];
  const dayCandles = history['1d'].slice(-1)[0] || last;
  state[symbol] = {
    price: last.close,
    prevClose: dayCandles.open,
    dayHigh: dayCandles.high,
    dayLow: dayCandles.low,
    dayOpen: dayCandles.open,
    volume: dayCandles.volume,
    candles: history,
    rand: seedRand(hashCode(symbol) + Date.now())
  };
  listeners[symbol] = { ticker: [], kline: {}, depth: [], trades: [] };
  return state[symbol];
}

// Tick every second - update price + candle + notify listeners
function tick() {
  for (const sym of Object.keys(state)) {
    const st = state[sym];
    const asset = ASSETS[sym];
    // Small movement per tick (clamped so a single sample can't spike the axis)
    let move = gauss(st.rand) * asset.vol * 0.15;
    const moveCap = asset.vol * 0.6;
    move = Math.max(-moveCap, Math.min(moveCap, move));
    st.price += move;
    // Glide toward the real anchor gently + clamped so the live candle eases
    // toward the real price instead of leaping (leaping rescaled the axis).
    const anchor = _realAnchor[sym];
    if (anchor && Number.isFinite(anchor.price)) {
      const diff = anchor.price - st.price;
      // Track the real anchor promptly (the big initial gap is already handled
      // by the one-time rebase in pollRealPrices, so ongoing diffs are small).
      // Cap at ~1% of price per tick so it stays smooth but never lags reality.
      const stepCap = Math.max(asset.vol * 3, Math.abs(anchor.price) * 0.01);
      st.price += Math.max(-stepCap, Math.min(stepCap, diff * 0.5));
      // Refresh day stats from the real snapshot
      if (Number.isFinite(anchor.dayHigh) && anchor.dayHigh > st.dayHigh) st.dayHigh = anchor.dayHigh;
      if (Number.isFinite(anchor.dayLow)  && anchor.dayLow  < st.dayLow)  st.dayLow  = anchor.dayLow;
      if (Number.isFinite(anchor.prevClose)) st.dayOpen = anchor.prevClose;
    }
    if (st.price < asset.base * 0.5) st.price = asset.base * 0.5;
    if (st.price > st.dayHigh) st.dayHigh = st.price;
    if (st.price < st.dayLow) st.dayLow = st.price;
    st.volume += Math.abs(move) / asset.vol * 20;

    // Update current 1m candle (and all intervals)
    const now = Math.floor(Date.now() / 1000);
    for (const iv of Object.keys(st.candles)) {
      const ivSec = intervalSeconds(iv);
      const barStart = Math.floor(now / ivSec) * ivSec;
      const arr = st.candles[iv];
      let last = arr[arr.length - 1];
      if (!last || last.time < barStart) {
        // new bar
        last = { time: barStart, open: st.price, high: st.price, low: st.price, close: st.price, volume: 0 };
        arr.push(last);
        if (arr.length > 400) arr.shift();
      } else {
        last.close = st.price;
        if (st.price > last.high) last.high = st.price;
        if (st.price < last.low) last.low = st.price;
        last.volume += Math.abs(move) / asset.vol * 5;
      }
      // Notify kline listeners
      (listeners[sym].kline[iv] || []).forEach(cb => cb({ ...last, closed: false }));
    }

    // Notify ticker
    const changePct = ((st.price - st.dayOpen) / st.dayOpen) * 100;
    const spread = asset.pip * 2;
    const tickerMsg = {
      symbol: sym,
      last: st.price,
      change: st.price - st.dayOpen,
      changePct,
      high: st.dayHigh,
      low: st.dayLow,
      volume: st.volume,
      quoteVolume: st.volume * st.price,
      bid: st.price - spread / 2,
      ask: st.price + spread / 2
    };
    listeners[sym].ticker.forEach(cb => cb(tickerMsg));

    // Notify orderbook: synthesize levels
    const bids = [], asks = [];
    for (let i = 0; i < 12; i++) {
      const p = st.price;
      const bidP = p - spread / 2 - i * asset.pip;
      const askP = p + spread / 2 + i * asset.pip;
      const qty = (0.3 + st.rand() * 3) * (i < 3 ? 3 : 1);
      bids.push({ price: bidP, qty });
      asks.push({ price: askP, qty });
    }
    listeners[sym].depth.forEach(cb => cb({ bids, asks }));

    // Trade tick (occasional)
    if (Math.random() < 0.35) {
      const qty = (0.05 + Math.random() * 0.8);
      const buyerMaker = Math.random() < 0.5;
      const trade = { id: Date.now() + Math.random(), price: st.price + (buyerMaker ? -asset.pip / 4 : asset.pip / 4), qty, time: Date.now(), buyerMaker };
      listeners[sym].trades.forEach(cb => cb(trade));
    }
  }
}

function ensureHeartbeat() {
  if (heartbeatStarted) return;
  heartbeatStarted = true;
  setInterval(tick, 1000);
  // Start real-price anchor poller
  ensureRealPricePoller();
}

// -------- Real-price anchor --------
const _realAnchor = {};  // symbol -> { price, prevClose, dayHigh, dayLow, ts }
// For WS-anchored symbols (XAUUSD/PAXG): keep the gold-api spot as the
// absolute level and overlay the sub-second PAXG delta on top so Buy/Sell
// matches the OANDA chart level while still updating tick-by-tick.
const _wsBridge = {};    // symbol -> { spot, wsRef, wsLast }
let _pollerStarted = false;

async function pollRealPrices() {
  const symbols = Object.keys(state);
  if (symbols.length === 0) return;
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const res = await axios.get(`${backend}/api/market/realtime-quotes`, {
      params: { symbols: symbols.join(',') },
      timeout: 8000
    });
    const quotes = (res.data && res.data.quotes) || {};
    for (const sym of Object.keys(quotes)) {
      const q = quotes[sym];
      if (!q || !Number.isFinite(q.price) || q.price <= 0) continue;
      _realAnchor[sym] = {
        price: q.price,
        prevClose: q.prevClose ?? q.price,
        dayHigh: q.dayHigh ?? q.price,
        dayLow:  q.dayLow  ?? q.price,
        change: q.change ?? 0,
        changePct: q.changePct ?? 0,
        ts: Date.now(),
        source: q.source
      };
      // If this symbol has a Binance WS bridge, snap the "spot reference" to
      // the current gold-api spot and capture the PAXG value at this moment
      // so the WS tick stream can emit (spot + PAXG delta) going forward.
      if (BINANCE_WS_ANCHORS[sym] && _wsBridge[sym]) {
        _wsBridge[sym].spot = q.price;
        _wsBridge[sym].wsRef = _wsBridge[sym].wsLast ?? _wsBridge[sym].wsRef;
      }
      // Rebase the synthetic history to the real price on first sight so the
      // chart sits at the real level with NO vertical jump / axis rescale.
      const st = state[sym];
      if (st && !st._realInit) {
        const c1m = st.candles['1m'];
        const lastClose = (c1m && c1m.length ? c1m[c1m.length - 1].close : q.price);
        const offset = q.price - lastClose;
        if (Number.isFinite(offset) && offset !== 0) {
          for (const iv of Object.keys(st.candles)) {
            st.candles[iv] = st.candles[iv].map(c => ({
              time: c.time,
              open: c.open + offset,
              high: c.high + offset,
              low: c.low + offset,
              close: c.close + offset,
              volume: c.volume
            }));
          }
        }
        st.price = q.price;
        st.dayOpen = q.prevClose ?? q.price;
        st.dayHigh = Math.max(q.dayHigh ?? q.price, q.price);
        st.dayLow  = Math.min(q.dayLow  ?? q.price, q.price);
        st._realInit = true;
      }
    }
  } catch (_) {
    // Silent — falls back to pure stochastic movement
  }
}

function ensureRealPricePoller() {
  if (_pollerStarted) return;
  _pollerStarted = true;
  // Fire once immediately after a short delay (let symbols register)
  setTimeout(pollRealPrices, 300);
  setInterval(pollRealPrices, REAL_POLL_MS);
  // Live Binance WS anchors (sub-second) for symbols where a tracking token
  // exists. Updates _realAnchor so synth tick converges fast + emits a direct
  // ticker event so Buy/Sell button prices track the chart in real time.
  for (const sym of Object.keys(BINANCE_WS_ANCHORS)) {
    openBinanceAnchor(sym, BINANCE_WS_ANCHORS[sym]);
  }
}

function openBinanceAnchor(internalSym, binanceSym) {
  let ws;
  let backoff = 1000;
  _wsBridge[internalSym] = _wsBridge[internalSym] || { spot: null, wsRef: null, wsLast: null };
  const connect = () => {
    try {
      ws = new WebSocket(`wss://stream.binance.com:9443/ws/${binanceSym}@ticker`);
    } catch (_) {
      setTimeout(connect, backoff); backoff = Math.min(backoff * 2, 30000); return;
    }
    ws.onopen = () => { backoff = 1000; };
    ws.onmessage = (ev) => {
      let msg; try { msg = JSON.parse(ev.data); } catch (_) { return; }
      const raw = parseFloat(msg.c);
      if (!Number.isFinite(raw) || raw <= 0) return;
      const bridge = _wsBridge[internalSym];
      bridge.wsLast = raw;
      if (bridge.wsRef == null) bridge.wsRef = raw;
      // Hybrid: absolute level = gold-api spot (close to OANDA chart);
      // tick delta = PAXG movement since last gold-api snapshot. This gives
      // sub-second live cadence AND a price matching the chart.
      const px = (bridge.spot != null && bridge.wsRef != null)
        ? bridge.spot + (raw - bridge.wsRef)
        : raw;
      const open = px;  // 24h open not meaningful for hybrid; recomputed later
      _realAnchor[internalSym] = {
        price: px, prevClose: _realAnchor[internalSym]?.prevClose ?? px,
        dayHigh: _realAnchor[internalSym]?.dayHigh ?? px,
        dayLow: _realAnchor[internalSym]?.dayLow ?? px,
        change: 0, changePct: 0,
        ts: Date.now(), source: `binance:${binanceSym}+spot`
      };
      const st = state[internalSym];
      if (st) {
        if (!st._realInit) {
          const c1m = st.candles['1m'];
          const lastClose = c1m && c1m.length ? c1m[c1m.length - 1].close : px;
          const offset = px - lastClose;
          if (Number.isFinite(offset) && offset !== 0) {
            for (const iv of Object.keys(st.candles)) {
              st.candles[iv] = st.candles[iv].map(c => ({
                time: c.time, open: c.open + offset, high: c.high + offset,
                low: c.low + offset, close: c.close + offset, volume: c.volume
              }));
            }
          }
          st.dayOpen = px;
          st.dayHigh = px;
          st.dayLow  = px;
          st._realInit = true;
        }
        st.price = px;
        if (px > st.dayHigh) st.dayHigh = px;
        if (px < st.dayLow)  st.dayLow  = px;
        const asset = ASSETS[internalSym];
        const spread = (asset && asset.pip ? asset.pip : 0.01) * 2;
        const tickerMsg = {
          symbol: internalSym, last: px,
          change: px - st.dayOpen,
          changePct: st.dayOpen ? ((px - st.dayOpen) / st.dayOpen) * 100 : 0,
          high: st.dayHigh, low: st.dayLow,
          volume: st.volume, quoteVolume: st.volume * px,
          bid: px - spread / 2, ask: px + spread / 2
        };
        (listeners[internalSym]?.ticker || []).forEach(cb => cb(tickerMsg));
      }
    };
    ws.onclose = () => { setTimeout(connect, backoff); backoff = Math.min(backoff * 2, 30000); };
    ws.onerror = () => { try { ws.close(); } catch (_) {} };
  };
  connect();
}

// --------- Public API (Binance-shaped) ----------
export async function synthFetchKlines(symbol, interval, limit = 300) {
  const st = ensureState(symbol);
  ensureRealPricePoller();
  // Anchor to the real price BEFORE first paint so the chart draws at the real
  // level (rebasing history) instead of jumping once the first poll lands.
  if (!st._realInit) {
    try { await pollRealPrices(); } catch (_) {}
  }
  const arr = st.candles[interval] || generateHistory(symbol, interval, limit);
  return arr.slice(-limit).map(c => ({ ...c }));
}

export async function synthFetch24hr(symbol) {
  const st = ensureState(symbol);
  return {
    lastPrice: st.price,
    priceChangePercent: ((st.price - st.dayOpen) / st.dayOpen) * 100,
    highPrice: st.dayHigh,
    lowPrice: st.dayLow,
    quoteVolume: st.volume * st.price,
    bidPrice: st.price - ASSETS[symbol].pip,
    askPrice: st.price + ASSETS[symbol].pip
  };
}

export async function synthFetchDepth(symbol) {
  ensureState(symbol);
  const st = state[symbol];
  const asset = ASSETS[symbol];
  const bids = [], asks = [];
  for (let i = 0; i < 20; i++) {
    bids.push([st.price - asset.pip - i * asset.pip, (0.3 + Math.random() * 3).toString()]);
    asks.push([st.price + asset.pip + i * asset.pip, (0.3 + Math.random() * 3).toString()]);
  }
  return { bids, asks };
}

export function synthSubscribeKline(symbol, interval, onMsg) {
  ensureState(symbol);
  ensureHeartbeat();
  listeners[symbol].kline[interval] = listeners[symbol].kline[interval] || [];
  listeners[symbol].kline[interval].push(onMsg);
  return { close: () => {
    const arr = listeners[symbol].kline[interval] || [];
    const i = arr.indexOf(onMsg);
    if (i >= 0) arr.splice(i, 1);
  } };
}

export function synthSubscribeTicker(symbol, onMsg) {
  ensureState(symbol);
  ensureHeartbeat();
  listeners[symbol].ticker.push(onMsg);
  return { close: () => {
    const i = listeners[symbol].ticker.indexOf(onMsg);
    if (i >= 0) listeners[symbol].ticker.splice(i, 1);
  } };
}

export function synthSubscribeDepth(symbol, onMsg) {
  ensureState(symbol);
  ensureHeartbeat();
  listeners[symbol].depth.push(onMsg);
  return { close: () => {
    const i = listeners[symbol].depth.indexOf(onMsg);
    if (i >= 0) listeners[symbol].depth.splice(i, 1);
  } };
}

export function synthSubscribeTrades(symbol, onMsg) {
  ensureState(symbol);
  ensureHeartbeat();
  listeners[symbol].trades.push(onMsg);
  return { close: () => {
    const i = listeners[symbol].trades.indexOf(onMsg);
    if (i >= 0) listeners[symbol].trades.splice(i, 1);
  } };
}

export function synthSubscribeMulti(symbols, onMsg) {
  const closers = symbols.map(sym => synthSubscribeTicker(sym, (t) => onMsg(t)));
  return { close: () => closers.forEach(c => c.close()) };
}

export const SYNTH_ASSETS = Object.entries(ASSETS).map(([sym, a]) => ({
  symbol: sym,
  display: a.name,
  pricePrecision: a.prec,
  class: a.class,
  provider: 'synth'
}));

export function getSynthAsset(symbol) {
  return ASSETS[symbol];
}
